import React, { useEffect } from 'react';
import { Col, Form, Row } from 'antd';
import { useTranslation } from 'react-i18next';
import { TextArea, Select, Popup, Input, Button, BUTTON_TYPES } from '_atoms';
import { Typography } from '_atoms/Typography';
import { JOB_SUBMIT_OPTIONS, REJECTION_DECISIONS } from 'constants/job';
import type { JobSubmitOption } from 'constants/job';
import { BUTTON_TEXT } from 'constants/text';
import { EXPERIENCE_LEVEL_OPTIONS } from '_organisms/InterviewFeedback/constants';
import { useDecisionOptions, useRejectionOptions, dataMapper } from './utils';
import './style.scss';

import type { DecisionOption } from './utils';

const FORM_NAME = 'form-submit-decision';

interface SubmitDecisionUserData {
  full_name_latin?: string;
  last_name_latin?: string;
  first_name_latin?: string;
  full_name_local?: string;
  last_name_local?: string;
  first_name_local?: string;
  speciality?: string | null;
}

interface PopupSubmitDecisionProps {
  open: boolean;
  onClose: () => void;
  onSave: (isFinalDecision: boolean, data: ReturnType<typeof dataMapper>) => void;
  userData: SubmitDecisionUserData;
  activeStep: number;
  isFinalDecision: boolean;
  candidateLink?: string;
  showHrInterviewWarning?: boolean;
}

interface SubmitDecisionFormValues {
  decision?: JobSubmitOption;
  rejection?: string;
  probationPeriod?: string;
  level?: DecisionOption;
  customLevel?: string;
  conditions?: string;
  comment?: string;
}

export const PopupSubmitDecision: React.FC<PopupSubmitDecisionProps> = ({
  open,
  onClose,
  onSave,
  userData,
  activeStep,
  isFinalDecision,
  candidateLink,
  showHrInterviewWarning,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<SubmitDecisionFormValues>();
  const decision = Form.useWatch('decision', form) as JobSubmitOption | undefined;
  const level = Form.useWatch('level', form) as DecisionOption | undefined;
  const period = Form.useWatch('probationPeriod', form) as string | undefined;
  const isManagementApproval =
    (decision === JOB_SUBMIT_OPTIONS.APPROVE || decision === JOB_SUBMIT_OPTIONS.APPROVE_WITH_CONDITIONS) &&
    activeStep === 4;

  const onCloseClick = () => {
    form.resetFields();
    onClose();
  };

  const onSubmit = ({
    decision: submittedDecision,
    rejection,
    probationPeriod = '',
    level: submittedLevel,
    customLevel = '',
    comment = '',
    conditions = '',
  }: SubmitDecisionFormValues) => {
    let reason = '';
    if (submittedDecision !== JOB_SUBMIT_OPTIONS.APPROVE) reason = rejection ?? '';
    onSave(
      isFinalDecision,
      dataMapper(isManagementApproval, reason, comment, conditions, submittedDecision, {
        probation_period: probationPeriod,
        technical_level: submittedLevel?.label,
        custom_technical_level: customLevel,
      })
    );
    onCloseClick();
  };

  const decisionOptions = useDecisionOptions(activeStep);
  const rejectionOptions = useRejectionOptions(activeStep, decision);

  useEffect(() => {
    if (decision) {
      form.setFieldValue('rejection', rejectionOptions?.[0]?.value);
    }
  }, [decision, form, rejectionOptions]);

  return (
    <Popup
      open={open}
      title={
        isFinalDecision
          ? t('translation:popup_submit_decision.title_final_decision')
          : t('translation:popup_submit_decision.title_submit_decision')
      }
      cancelButtonProps={{
        onClick: onCloseClick,
        children: BUTTON_TEXT.CANCEL,
      }}
      okButtonProps={{
        children: BUTTON_TEXT.SUBMIT,
        form: FORM_NAME,
        htmlType: 'submit',
        disabled: isManagementApproval ? !decision || !level || !period : !decision,
      }}
      className="decision-popup mobile-full-size"
    >
      <Form name={FORM_NAME} form={form} validateTrigger="onSubmit" onFinish={onSubmit}>
        <div className="user-info">
          <Typography.Text className="user-info__title">
            {userData.full_name_latin || `${userData.first_name_latin} ${userData.last_name_latin}`}
          </Typography.Text>
          <Typography.Caption className="user-info__subtitle">
            {userData.full_name_local || `${userData.first_name_local} ${userData.last_name_local}`}
          </Typography.Caption>
          <Typography.Caption className="user-info__subtitle">{userData.speciality}</Typography.Caption>
          {candidateLink && (
            <a target="_blank" href={candidateLink} rel="noreferrer">
              <Button type={BUTTON_TYPES.TEXT}>{t('translation:popup_submit_decision.button_view_candidate')}</Button>
            </a>
          )}
        </div>
        {showHrInterviewWarning && (
          <p className="decision-popup__warning">
            <strong>{t('translation:popup_submit_decision.hr_interview_warning_bold')}</strong>
            {t('translation:popup_submit_decision.hr_interview_warning_text')}
          </p>
        )}
        <Select
          label={t('translation:popup_submit_decision.label_decision')}
          placeholder={t('translation:popup_submit_decision.ph_select_decision')}
          options={decisionOptions}
          itemProps={{ name: 'decision' }}
          className="form-row"
        />
        {REJECTION_DECISIONS.some(rejectionDecision => rejectionDecision === decision) && (
          <Select
            label={t('translation:popup_submit_decision.label_rejection_reason')}
            placeholder={t('translation:popup_submit_decision.ph_select_rejection_reason')}
            options={rejectionOptions}
            itemProps={{ name: 'rejection' }}
          />
        )}
        {decision === JOB_SUBMIT_OPTIONS.APPROVE_WITH_CONDITIONS && (
          <TextArea
            label={t('translation:popup_submit_decision.label_conditions')}
            maxLength={2000}
            placeholder={t('translation:popup_submit_decision.ph_enter_conditions')}
            itemProps={{
              name: 'conditions',
              rules: [{ required: true, whitespace: true, message: t('translation:validation.required') }],
            }}
            className="form-row"
            rows={3}
          />
        )}
        {isManagementApproval && (
          <>
            <Row justify="space-between" align="bottom" gutter={4}>
              <Col span={19}>
                <Input
                  label={t('translation:popup_submit_decision.label_probation_period')}
                  placeholder={t('translation:popup_submit_decision.ph_number_of_months')}
                  itemProps={{
                    name: 'probationPeriod',
                    normalize: (value: string) => value.replace(/[^0-9]/g, '').slice(-1),
                  }}
                />
              </Col>
              <Col span={5}>
                <Typography.Text className="probation-period-text">
                  {t('translation:popup_submit_decision.text_months')}
                </Typography.Text>
              </Col>
            </Row>
            <Row gutter={12} align="bottom">
              <Col span={level?.id === 6 ? 12 : 24}>
                <Select
                  label={t('translation:popup_submit_decision.label_assumed_experience_level')}
                  options={EXPERIENCE_LEVEL_OPTIONS}
                  placeholder={t('translation:popup_submit_decision.ph_select_assumed_technical_level')}
                  labelInValue
                  itemProps={{ name: 'level' }}
                  onChange={() => form.setFieldValue('customLevel', undefined)}
                />
              </Col>
              {level?.id === 6 && (
                <Col span={12}>
                  <Input
                    placeholder={t('translation:popup_submit_decision.ph_level')}
                    itemProps={{ name: 'customLevel' }}
                    maxLength={20}
                  />
                </Col>
              )}
            </Row>
          </>
        )}
        <TextArea
          label={t('translation:popup_submit_decision.label_comment')}
          maxLength={6000}
          placeholder={t('translation:popup_submit_decision.ph_enter_comment')}
          itemProps={{ name: 'comment' }}
          rows={3}
        />
      </Form>
    </Popup>
  );
};
